import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Button, Header, Input, Segment, Grid, Container } from "semantic-ui-react";
import { LanguageDto } from "../../models/languageDto";
import agent from "../../api/agent";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useStore } from "../../stores/store";
import parse from "html-react-parser";
import MedalImage from "./medalImage";
import CertificateBackground from "./CertificateBackground";
import ADBLogo from "./ADBLogo";
import CertificateSignature from "./CertificateSignature";

interface Params {
  surveyCode: string;
  userSurveyCode: string;
}

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export default function DownloadCertificate() {
  let { surveyCode, userSurveyCode } = useParams<Params>();
  const { t } = useTranslation("common");
  const { i18n } = useTranslation("common");
  const query = useQuery();
  const { commonStore } = useStore();
  const [inEditMode, setInEditMode] = useState<boolean>(true);
  const [participant, setParticipant] = useState<string>("");
  const [reportLanguages, setReportLanguages] = useState<LanguageDto[]>();
  const [loadingLanguages, setLoadingLanguages] = useState<boolean>(true);
  const [downloadingFile, setDownloadingFile] = useState<boolean>(false);
  const [pageLoading, setPageLoading] = useState<boolean>(true);

  useEffect(() => {
    if (commonStore.currentSurveyId === 0) {
      agent.Surveys.info(surveyCode)
        .then((result) => {
          commonStore.setCurrentSurveyId(result.id);
          let lang = query.get("language");
          if (lang) {
            commonStore.setLanguage(lang);
            i18n.changeLanguage(lang).then(() => {
              i18n.reloadResources();
              setPageLoading(false);
            });
          } else setPageLoading(false);
        })
        .catch((error) => {
          toast.error("Failed to load survey languages !");
        });
    }

    agent.UserSurveys.languages(userSurveyCode)
      .then((result) => {
        if (result) {
          let filteredLanguages = result.filter((l) => l.locale != "kh");
          setReportLanguages(filteredLanguages);
        }
      })
      .catch((error) => {
        toast.error("An error has occurred while loading report languages");
      })
      .finally(() => {
        setLoadingLanguages(false);
      });
  }, [userSurveyCode]);

  const downloadCertificate = (language: string) => {
    setDownloadingFile(true);
    if (language === commonStore.lang) {
      downloadFile();
      return;
    }

    commonStore.setLanguage(language);
    i18n.changeLanguage(language).then(() => {
      downloadFile();
    });
  };

  const downloadFile = () => {
    const staticElement: HTMLElement | null = document.getElementById(`userCertificate`);
    if (staticElement == null) return;
    agent.UserSurveys.downloadCertificate(userSurveyCode, participant, staticElement.innerHTML, t);
    setDownloadingFile(false);
  };

  const certificate = (
    <>
      <Segment>
        {!inEditMode && (
          <Button
            content={t("Certificate.Edit")}
            onClick={() => {
              setInEditMode(true);
            }}
          ></Button>
        )}
        {inEditMode && (
          <>
            <>{t("Certificate.EnterYourName")}</> &nbsp;
            <>{t("Certificate.OnceConfirmed")}</> &nbsp;
            <br />
            <br />
            <Input
              type="text"
              style={{ marginRight: "10px" }}
              value={participant}
              onChange={(e) => {
                setParticipant(e.target.value);
              }}
            ></Input>
          </>
        )}
        {!inEditMode && (
          <Button
            className="icon"
            icon="download"
            as="div"
            labelPosition="left"
            content={t("Common.Download")!}
            loading={downloadingFile || loadingLanguages}
            onClick={() => downloadCertificate(commonStore.lang ? commonStore.lang : "en")}
          ></Button>
        )}
        {inEditMode && (
          <Button
            content={t("Common.Confirm")}
            onClick={() => {
              setInEditMode(false);
            }}
            disabled={participant === ""}
          ></Button>
        )}
      </Segment>
      <div
        id="userCertificate"
        style={{
          background: "#fff",
          marginTop: "0px",
          marginLeft: "0px",
        }}
      >
        <Container
          textAlign="center"
          style={{
            background: "#fff",
          }}
        >
          <div
            className="cert-bg"
            style={{
              position: "absolute",
              display: "block",
              width: "1142px",
              height: "794px",
              marginTop: "-15px",
              marginLeft: "-15px",
            }}
          >
            <CertificateBackground />
          </div>

          <Segment
            style={{
              width: "1123px",
              height: "794px",
              background: "transparent",
              position: "relative",
              zIndex: "999",
            }}
          >
            <Grid
              style={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                alignItems: "stretch",
                padding: "0",
                height: "794px",
              }}
            >
              <Grid.Row
                style={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "row",
                  width: "100%",
                  padding: "0",
                }}
              >
                <Grid.Column
                  className="cert-header"
                  width={12}
                  style={{ width: "70%", paddingTop: "40px" }}
                >
                  <Header
                    as="h1"
                    style={{
                      color: "white",
                      marginBottom: "0px",
                      marginLeft: "50px",
                      fontSize: "5.8em",
                      textAlign: "left",
                      textTransform: "uppercase",
                    }}
                  >
                    {t("Certificate.Certificate")}
                  </Header>
                  <Header
                    as="h2"
                    style={{
                      color: "white",
                      marginBottom: "0px",
                      marginLeft: "50px",
                      fontSize: "3em",
                      textAlign: "left",
                      marginTop: "0px",
                    }}
                  >
                    {t("Certificate.OfParticipation")}
                  </Header>
                </Grid.Column>
                <Grid.Column width={4} style={{ width: "25%" }}>
                  <div className="medal" style={{ float: "right", marginTop: "-15px" }}>
                    <MedalImage />
                  </div>
                </Grid.Column>
              </Grid.Row>
              <Grid
                className="cert-grid-info"
                style={{ width: "100%", marginTop: "-65px", marginLeft: "-16px", paddingLeft: "0px" }}
              >
                <Grid.Row>
                  <div
                    style={{
                      textAlign: "center",
                      width: "100%",
                      backgroundColor: "#fff",
                      height: "150px",
                      marginBottom: "1rem",
                      display: "table",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "5em",
                        color: "#000",
                        textAlign: "center",
                        width: "100%",
                        display: "table-cell",
                        lineHeight: "74px",
                        verticalAlign: "middle",
                      }}
                    >
                      {participant}
                    </p>
                  </div>
                </Grid.Row>
                <Grid.Row
                  style={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    justifyContent: "inherit",
                    alignItems: "stretch",
                    width: "100%",
                    padding: "0",
                  }}
                >
                  <p style={{ fontSize: "18px", textAlign: "center", width: "100%", margin: "0 155px" }}>
                    {parse(t("Certificate.Completed"))}
                  </p>
                </Grid.Row>
                <Grid.Row
                  style={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    justifyContent: "inherit",
                    alignItems: "stretch",
                    width: "100%",
                    padding: "0",
                  }}
                >
                  <Grid.Column width={3} style={{ width: "18%" }}></Grid.Column>
                  <Grid.Column
                    width={5}
                    style={{ width: "30%", paddingLeft: "1rem", paddingReft: "1rem" }}
                  >
                    <p style={{ fontSize: "1.4em", marginBottom: "5px", textAlign: "center" }}>
                      <span style={{ width: "100px" }}>
                        <CertificateSignature />
                      </span>
                    </p>
                    <p
                      style={{
                        borderBottom: "1px solid #005596",
                        fontSize: "1.4em",
                        marginBottom: "0px",
                      }}
                    ></p>
                    <p style={{ fontSize: "1.4em", minHeight: "40px", textAlign: "center" }}>
                      {t("Certificate.SeniorEducationSpecialist")}
                    </p>
                  </Grid.Column>
                  <Grid.Column
                    width={5}
                    style={{ width: "30%", paddingLeft: "1rem", paddingReft: "1rem" }}
                  >
                    <p style={{ height: "35px", marginBottom: "5px" }}></p>
                    <p
                      className="date-data"
                      style={{ fontSize: "1.4em", marginBottom: "5px", textAlign: "center" }}
                    >
                      {new Date().toDateString()}
                    </p>
                    <p
                      style={{
                        borderBottom: "1px solid #005596",
                        fontSize: "1.4em",
                        marginBottom: "0px",
                      }}
                    ></p>
                    <p style={{ fontSize: "1.4em", minHeight: "40px", textAlign: "center" }}>
                      {t("Certificate.Date")}
                    </p>
                  </Grid.Column>
                  <Grid.Column width={1} style={{ width: "4%" }}></Grid.Column>
                  <Grid.Column
                    width={2}
                    style={{ width: "12%", paddingReft: "1rem", paddingTop: "80px" }}
                  >
                    <ADBLogo />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Grid>
          </Segment>
        </Container>
      </div>
    </>
  );

  return <>{!pageLoading && certificate}</>;
}
